import React, { useEffect, useState } from "react";
import axios from "axios";
import { MDBRow, MDBCol, MDBCardBody, MDBSpinner } from "mdb-react-ui-kit";

function CommitteeStaff({ staffMembers, committeeCode, customMembers }) {
  const [staff, setStaff] = useState([]);
  const [sortedStaff, setSortedStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Don’t combine or sort until the fetch has finished
    if (isLoading) return;
    // Combine staff data
    let combinedMembersStaff = (staffMembers || []).map((member) => {
      const staffMember = staff.find((l) => l.id === member.staffId);
      return { ...member, ...staffMember };
    });

    // Append custom members for CWH or CWS
    const customMembersObject = {
      CWH: [
        {
          firstName: "Isaiah",
          lastName: "Kopczuk",
          staffMembershipType: { name: "House Reading Clerk", code: "CUSTOM" },
        },
        {
          firstName: "Caitlin",
          lastName: "Wagoner",
          staffMembershipType: { name: "House Journal Clerk", code: "CUSTOM" },
        },
        {
          firstName: "Joanne",
          lastName: "Kauzlarich",
          staffMembershipType: { name: "House Status Clerk", code: "CUSTOM" },
        },
        {
          firstName: "Joanne",
          lastName: "Austin",
          staffMembershipType: { name: "House Bills Clerk", code: "CUSTOM" },
        },
        {
          firstName: "Brittany",
          lastName: "Bury",
          staffMembershipType: {
            name: "House Bills Coordinator",
            code: "CUSTOM",
          },
        },
        {
          firstName: "Trace",
          lastName: "Walker",
          staffMembershipType: { name: "Vote Clerk", code: "CUSTOM" },
        },
      ],
      CWS: [
        {
          firstName: "Brent",
          lastName: "Webber",
          staffMembershipType: { name: "Senate Reading Clerk", code: "CUSTOM" },
        },
        {
          firstName: "Penny",
          lastName: "Hopkins",
          staffMembershipType: { name: "Senate Journal Clerk", code: "CUSTOM" },
        },
        {
          firstName: "George",
          lastName: "Panek",
          staffMembershipType: {
            name: "Senate Bill Status Clerk",
            code: "CUSTOM",
          },
        },
        {
          firstName: "Kathy",
          lastName: "Baird",
          staffMembershipType: { name: "Senate Bills Clerk", code: "CUSTOM" },
        },
        {
          firstName: "Lynn",
          lastName: "Staley",
          staffMembershipType: {
            name: "Senate Bills Coordinator",
            code: "CUSTOM",
          },
        },
        {
          firstName: "Benjamin",
          lastName: "Barker",
          staffMembershipType: { name: "Senate Vote Clerk", code: "CUSTOM" },
        },
      ],
      LCC: [
        {
          firstName: "Suzanne",
          lastName: "Snow",
          staffMembershipType: { name: "Secretary", code: "CUSTOM" },
        },
        {
          firstName: "Jason",
          lastName: "Brown",
          staffMembershipType: { name: "Research Analyst", code: "CUSTOM" },
        },
      ],
    };

    // Append custom members for CWH or CWS
    if (committeeCode && customMembersObject[committeeCode.code]) {
      combinedMembersStaff = [
        ...combinedMembersStaff,
        ...customMembersObject[committeeCode.code],
      ];
    }

    // For ITPC, strip Jerry Howe out of the staff list as he has been added to Members
    if (committeeCode?.code === "ITPC") {
      combinedMembersStaff = combinedMembersStaff.filter(
        (m) => m.staffMembershipType?.code === "SE"
      );
    }

    // Define the order of staff titles
    const staffOrder = [
      "SOS", //Secretary of the Senate
      "COH", //Chief Clerk of the House
      "ED", //Legislative Services Director
      "LSD", //Legislative Services Director
      "LFA", //Legislative Fiscal Analyst
      "LA", //Legislative Auditor
      "LDD", //Legal Services Director
      "LCC", //Legal Services Director
      "RD", //Research Director
      "CIO", //Chief Information Officer
      "DLD", //Deputy Legal Services Director
      "AT", //Attorney
      "DFA", //Deputy Fiscal Analyst
      "DLA", //Deputy Legislative Auditor
      "DRD", //Deputy Research Director
      "FLA", //Lead Fiscal Analyst
      "FA", //Fiscal Analyst
      "LEA", //Environmental Analyst
      "RA", //Research Analyst
      "AU", //Auditor
      "OS", //OBPP Staffer
      "SE", //Secretary
      "CL", //Clerk
      "RMC", //Remote Meeting Coordinator
      "CUSTOM", // Custom members
    ];

    // Sort by staff title order, then alphabetically
    combinedMembersStaff.sort((a, b) => {
      const titleA = a?.staffMembershipType?.code || "";
      const titleB = b?.staffMembershipType?.code || "";

      const indexA = staffOrder.indexOf(titleA);
      const indexB = staffOrder.indexOf(titleB);

      if (indexA !== indexB) {
        return (
          (indexA === -1 ? Infinity : indexA) -
          (indexB === -1 ? Infinity : indexB)
        );
      }

      return (
        (a.lastName || "").localeCompare(b.lastName || "") ||
        (a.firstName || "").localeCompare(b.firstName || "")
      );
    });

    setSortedStaff(combinedMembersStaff);
  }, [staff, staffMembers, committeeCode, isLoading]);

  useEffect(() => {
    const fetchStaff = async () => {
      const staffMemberIds = (staffMembers || []).map((staff) => staff.staffId);
      // Fetch exactly as many as you need
      const limit = staffMemberIds.length || 1;
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/staffMembers/search?limit=${limit}&offset=0`,
          { staffMemberIds }
        );
        setStaff(response.data.content || []);
      } catch (error) {
        console.error("Error fetching staff details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStaff();
  }, [staffMembers]);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  return (
    <MDBRow id="staff" className="row-cols-1 row-cols-md-2 g-3 mb-4 pt-2">
      {sortedStaff.map((staffMember, index) => (
        <MDBCol key={index}>
          <MDBRow>
            <MDBCol>
              <MDBCardBody className="p-0">
                <h6 className="card-title mb-1 text-dark">
                  {staffMember.lastName}, {staffMember.firstName}
                </h6>
                <h6 className="sub-heading m-0 text-muted">
                  {staffMember?.staffMembershipType?.name ||
                    "No Membership Type"}
                </h6>
              </MDBCardBody>
            </MDBCol>
          </MDBRow>
        </MDBCol>
      ))}
    </MDBRow>
  );
}

export default CommitteeStaff;
